import { task } from 'mobx-task'
import type { FetchJobFn } from '@/features/jobs'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { CallCenterJobViewModel } from '@/features/call-center/JobSection/CallCenterJobViewModel'
import { JobNotSupportedError } from '@/features/call-center/job-not-supported-error'

/**
 * Loads a job into the call center.
 */
export class CallCenterJobPageViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    readonly jobId: Id,
    private readonly _fetchJob: FetchJobFn,
    private readonly _jobVm: CallCenterJobViewModel,
    private readonly _openJob: (jobId: Id) => void,
  ) {}

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this._openJob(this.jobId)
    // The page renders the failure, including an unsupported job.
    void this.fetch().catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  fetch = task(async () => {
    const signal = this._lifecycle.signal
    const job = await this._fetchJob(this.jobId, undefined, signal)
    signal.throwIfAborted()

    if (!job.isDraft || job.legendServiceType !== 'Tax') {
      throw new JobNotSupportedError()
    }

    await this._jobVm.openExistingJob(job, signal)
  })
}
